/**
 * Repository
 * {
 *   elements: {
 *     user_1: {
 *       userId: 'user_1',
 *     },
 *     user_2: {
 *       userId: 'user_2',
 *     },
 *   },
 *   mapFields: {
 *     spaceRoot: {
 *       references: {
 *         space_1: { userId: 'user_1' } reference,
 *       },
 *       isStale: true;
 *       listener: 1,
 *       cachedAt: 564582151
 *     }
 *     spaceMembership: {
 *       references: {
 *         user_1: { userId: 'user_1' } reference,
 *         user_2: { userId: 'user_2' } reference,
 *       },
 *       isStale: true;
 *       listener: 1,
 *       cachedAt: 564582151
 *     }
 *   },
 *   listFields: {
 *     friends: {
 *       references: [{ userId: 'user_2' } reference],
 *       cursor: 'cursor......',
 *       isStale: true;
 *       listener: 0,
 *       cachedAt: 564582151,
 *       cacheLimit: 20,
 *       isUnique: true
 *     },
 *   },
 * }
 */

import {
  clone,
  cloneDeep,
  isEqual,
  mapValues,
  orderBy,
  partition,
  transform,
} from 'lodash';

import { NonEmptyArray } from '../Utils';
import { array } from '../../utils/array';
import { numberToDate } from '../../utils/date';

const DEFAULT_CACHE_LIMIT = 100;
const DEFAULT_FIELD_LIMIT = 100;

export type Cursor = string | undefined | null; // null: reach end;

export interface RepositoryMapField<E, T = number> {
  references: Record<string, E | null>;
  isStale: boolean;
  listener: number; // if 0, will set cached at;
  cachedAt: T;
}

export interface RepositoryListField<E, T = number> {
  references: E[]; // saving the reference of element map
  cursor: Cursor;
  isStale: boolean;
  listener: number; // if 0, will set cached at;
  cachedAt: T;
  readonly cacheLimit: number;
  readonly isUnique: boolean;
}

export abstract class RepositoryElement {
  abstract id(): string;

  update(element: this): void {
    if (
      !('lastModifiedAt' in element) ||
      !('lastModifiedAt' in this) ||
      (element.lastModifiedAt as Date) >= (this.lastModifiedAt as Date)
    ) {
      const c = element.clone();
      const keys = new Set([...Object.keys(this), ...Object.keys(c)]) as Set<
        keyof this
      >;
      keys.forEach(<K extends keyof this>(key: K) => {
        if (!isEqual(this[key], c[key])) {
          this[key] = c[key];
        }
      });
    } else {
      console.warn('skip updating stale element');
      console.warn(element);
    }
  }

  clone(): this {
    return cloneDeep(this);
  }
}

export interface RepositoryJson<
  E,
  M extends string,
  L extends string,
  T = number,
> {
  elements?: Record<string, E>;
  mapFields?: Record<M, Partial<RepositoryMapField<string, T>>>;
  listFields?: Record<L, Partial<RepositoryListField<string, T>>>;
  fieldLimit?: number;
}

type FieldResult<F, O> = O extends null ? undefined | F : F;

export enum FieldType {
  Lists = 'lists',
  Maps = 'maps',
}

export type MapField<E, O = undefined> = FieldResult<
  {
    get(key: string): E | null | undefined;
    getAll(): Record<string, E | null | undefined>;
    set(key: string, element: E | null): void;
    delete(key: string): void;
    isStale(): boolean;
    setIsStale(isStale: boolean): void;
    isCached(): boolean;
    addListener(): void;
    removeListener(): void;
  },
  O
>;

export type ListField<E, O = undefined> = FieldResult<
  {
    get(): E[];
    set(...elements: E[]): void;
    setFrom(from: string | NonEmptyArray<string>, ...elements: E[]): void;
    push(...elements: E[]): void;
    pushFrom(from: string | NonEmptyArray<string>, ...elements: E[]): void;
    unshift(...elements: E[]): void;
    unshiftFrom(from: string | NonEmptyArray<string>, ...elements: E[]): void;
    splice(start: number, deleteCount: number, ...elements: E[]): void;
    prune(): void;
    getCursor(): Cursor;
    setCursor(cursor: Cursor): void;
    isStale(): boolean;
    setIsStale(isStale: boolean): void;
    isCached(): boolean;
    addListener(): void;
    removeListener(): void;
  },
  O
>;

export type ListFields<E, O = undefined> = FieldResult<
  {
    set(...elements: E[]): void;
    setFrom(from: string | NonEmptyArray<string>, ...elements: E[]): void;
    push(...elements: E[]): void;
    pushFrom(from: string | NonEmptyArray<string>, ...elements: E[]): void;
    unshift(...elements: E[]): void;
    unshiftFrom(from: string | NonEmptyArray<string>, ...elements: E[]): void;
    splice(start: number, deleteCount: number, ...elements: E[]): void;
    prune(): void;
    setCursor(cursor: Cursor): void;
    setIsStale(isStale: boolean): void;
    addListener(): void;
    removeListener(): void;
  },
  O
>;

export default class Repository<
  E extends RepositoryElement,
  P = E,
  M extends string = string,
  L extends string = string,
> {
  /**
   * When the fields have exceeded the cache limits, things may be pruned to keep memory usage reasonable,
   * we use the adding auto-reference counting (ARC) mechanism to optimized performance to prevent iterate
   * everything to see if an element needs to be pruned from any field.
   *
   * We are creating a table to keep track of which fields have references to elements. If there are no
   * fields referencing an element, it will be automatically deleted to optimize memory usage.
   */
  private table: Record<
    string,
    {
      element: E;
      [FieldType.Maps]: Record<M, number>;
      [FieldType.Lists]: Record<L, number>;
    } | null
  >;
  protected mapFields: Record<M, RepositoryMapField<E, Date>>;
  protected listFields: Record<L, RepositoryListField<E, Date>>;
  protected readonly fieldLimit: number; // limit for cached listFields
  onElementPrune?: (element: E) => void;

  static initFields<
    N extends string,
    O extends N,
    F extends
      | RepositoryMapField<string, Date | number>
      | RepositoryListField<string, Date | number>,
  >(
    fields: Record<N, Partial<F>> = {} as Record<N, Partial<F>>,
    options: Record<O, Partial<F> & { references?: never }>,
  ): Record<N, Partial<F>> {
    Object.entries<Partial<F>>(options).forEach(
      ([fieldName, { listener = 1, ...rest }]) => {
        fields[fieldName as N] = {
          ...rest,
          ...(fields[fieldName as N] ?? {}),
          listener,
        } as Partial<F>;
      },
    );
    return fields;
  }

  constructor(
    {
      elements,
      mapFields,
      listFields,
      fieldLimit,
    }: RepositoryJson<P, M, L, Date | number>,
    constructor: (p: P) => E,
  ) {
    this.table = mapValues(elements, (element) => ({
      element: constructor(element),
      [FieldType.Maps]: {} as Record<M, number>,
      [FieldType.Lists]: {} as Record<L, number>,
    }));
    this.mapFields = mapValues(
      mapFields,
      ({ references, isStale, listener, cachedAt }, fieldName) => {
        const r: Record<string, E | null> = {};
        Object.entries(references ?? {}).forEach(([key, referenceId]) => {
          const element =
            referenceId === null
              ? null
              : this.addReferenceInFields(
                  FieldType.Maps,
                  [fieldName as M],
                  referenceId,
                );
          if (element !== undefined) {
            r[key] = element;
          }
        });
        return {
          references: r,
          isStale: isStale ?? true,
          listener: listener ?? 0,
          cachedAt: cachedAt ? clone(numberToDate(cachedAt)) : new Date(),
        };
      },
    );
    this.listFields = mapValues(
      listFields,
      (
        {
          references,
          cursor,
          isStale,
          listener,
          cachedAt,
          cacheLimit,
          isUnique,
        },
        fieldName,
      ) => ({
        references:
          references?.reduce((acc, referenceId) => {
            const element = this.addReferenceInFields(
              FieldType.Lists,
              [fieldName as L],
              referenceId,
            );
            if (element) {
              acc.push(element);
            }
            return acc;
          }, [] as E[]) ?? [],
        cursor,
        isStale: isStale ?? true,
        listener: listener ?? 0,
        cachedAt: cachedAt ? clone(numberToDate(cachedAt)) : new Date(),
        cacheLimit: cacheLimit ?? DEFAULT_CACHE_LIMIT,
        isUnique: isUnique ?? true,
      }),
    );
    this.fieldLimit = fieldLimit ?? DEFAULT_FIELD_LIMIT;

    // prune element without references
    Object.entries(this.table).forEach(([key, value]) => {
      if (
        value &&
        Object.keys(value[FieldType.Maps]).length === 0 &&
        Object.keys(value[FieldType.Lists]).length === 0
      ) {
        delete this.table[key];
      }
    });
  }

  getElements(): E[] {
    return Object.values(this.table).reduce<E[]>((acc, item) => {
      if (item !== null) {
        acc.push(item.element);
      }
      return acc;
    }, []);
  }

  toJSON(): RepositoryJson<E, M, L, Date | number> {
    return {
      elements: transform(
        this.table,
        (result, value, key) => {
          // not persisting null element
          if (value) {
            result[key] = value.element;
          }
        },
        {} as Record<string, E>,
      ),
      mapFields: mapValues(this.mapFields, ({ references, cachedAt }) => ({
        references: mapValues(references, (reference) =>
          reference === null ? null : reference.id(),
        ),
        isStale: true,
        listener: 0, // reset listener to 0
        cachedAt: cachedAt.getTime(),
      })),
      listFields: mapValues(
        this.listFields,
        ({ references, cachedAt, cacheLimit, isUnique }) => ({
          references: references.map((reference) => reference.id()),
          cursor: undefined,
          isStale: true,
          listener: 0, // reset listener to 0
          cachedAt: cachedAt.getTime(),
          cacheLimit,
          isUnique,
        }),
      ),
      fieldLimit: this.fieldLimit,
    };
  }

  get(id: string): E | null | undefined {
    const value = this.table[id];
    return value ? value.element : value;
  }

  set(...elements: E[]) {
    elements.forEach((element) => {
      // add reference in no field to update element
      this.addReferenceInFields(FieldType.Maps, [], element);
    });
  }

  /**
   * Delete the elements with corresponding ids.
   * This deletes all references in the fields and sets null in the table.
   * Hence, new object with same id cannot be added again.
   * This is usually used when something needs to be deleted in the backend.
   * @param ids Items to be deleted
   */
  delete(...ids: string[]) {
    ids.forEach((id) => {
      const value = this.table[id];
      if (value) {
        this.forEachField(
          this.mapFields,
          Object.keys(value[FieldType.Maps]) as M[],
          (field, fieldName) => {
            const entries = Object.entries(field.references).filter(
              ([, reference]) => reference?.id() === id,
            );
            entries.forEach(([key]) => {
              delete field.references[key];
            });
            this.removeReferencesInField(
              FieldType.Maps,
              fieldName,
              entries.map(([, reference]) => reference as E),
            );
          },
        );

        this.forEachField(
          this.listFields,
          Object.keys(value[FieldType.Lists]) as L[],
          (field, fieldName) => {
            const [kept, removed] = partition(
              field.references,
              (reference) => reference.id() !== id,
            );
            field.references = kept;
            this.removeReferencesInField(FieldType.Lists, fieldName, removed);
          },
        );
      }
      this.table[id] = null;
    });
  }

  stale(...fieldNames: (M | L)[]) {
    const fns =
      fieldNames.length === 0
        ? Object.keys(this.mapFields).concat(Object.keys(this.listFields))
        : fieldNames;

    fns.forEach((fn) => {
      const mapField = this.mapFields[fn as M];
      if (mapField) {
        mapField.isStale = true;
      }

      const listField = this.listFields[fn as L];
      if (listField) {
        listField.isStale = true;
      }
    });
  }

  clear(...fieldNames: (M | L)[]) {
    const fns =
      fieldNames.length === 0
        ? Object.keys(this.mapFields).concat(Object.keys(this.listFields))
        : fieldNames;

    fns.forEach((fn) => {
      const mapField = this.mapFields[fn as M];
      if (mapField) {
        const removed = Object.values(mapField.references).filter(
          (e) => e !== null,
        ) as E[];
        mapField.references = {};
        this.removeReferencesInField(FieldType.Maps, fn as M, removed);
      }

      const listField = this.listFields[fn as L];
      if (listField) {
        const removed = listField.references;
        listField.references = [];
        this.removeReferencesInField(FieldType.Lists, fn as L, removed);
      }
    });
  }

  map<
    O extends
      | (Partial<RepositoryMapField<E, Date>> & { references?: never })
      | null
      | undefined = undefined,
  >(fieldName: M, options?: O): MapField<E, O> {
    const mapField = this.putFieldIfAbsent(
      this.mapFields,
      fieldName,
      options === null
        ? null
        : {
            isStale: true,
            listener: 0,
            cachedAt: new Date(),
            ...options,
            references: {},
          },
    );
    if (!mapField) {
      return undefined as MapField<E, O>;
    }

    return {
      get: (key) => mapField.references[key],
      getAll: () => mapField.references,
      set: (key, element) => {
        // update cached field cachedAt and get all uncached field
        const uncachedFieldNames: M[] = [];
        if (mapField.listener > 0) {
          uncachedFieldNames.push(fieldName);
        } else {
          mapField.cachedAt = new Date();
        }

        // only update existing elements if all cached, otherwise put if absent
        const e = element
          ? this.addReferenceInFields(
              FieldType.Maps,
              uncachedFieldNames,
              element,
            )
          : element;

        // update uncached field references
        if (e !== undefined && uncachedFieldNames.length > 0) {
          const oldReference = mapField.references[key];
          mapField.references[key] = e;
          if (oldReference) {
            this.removeReferencesInField(FieldType.Maps, fieldName, [
              oldReference,
            ]);
          }
        }
      },
      delete: (key) => {
        // update cached field cachedAt
        if (mapField.listener === 0) {
          mapField.cachedAt = new Date();
        }

        // remove references from both cached or uncached field
        const oldReference = mapField.references[key];
        delete mapField.references[key];
        if (oldReference) {
          this.removeReferencesInField(FieldType.Maps, fieldName, [
            oldReference,
          ]);
        }
      },
      isStale: () => mapField.isStale,
      setIsStale: (isStale: boolean) => {
        this.setIsStale(this.mapFields, [fieldName], isStale);
      },
      isCached: () => mapField.listener === 0,
      addListener: () => {
        this.addListener(this.mapFields, [fieldName]);
      },
      removeListener: () => {
        this.removeMapFieldListener([fieldName]);
      },
    };
  }

  list<
    O extends
      | (Partial<RepositoryListField<E, Date>> & { references?: never })
      | null
      | undefined = undefined,
  >(fieldName: L, options?: O): ListField<E, O> {
    const field = this.putFieldIfAbsent(
      this.listFields,
      fieldName,
      options === null
        ? null
        : {
            cursor: undefined,
            isStale: true,
            listener: 0,
            cachedAt: new Date(),
            cacheLimit: DEFAULT_CACHE_LIMIT,
            isUnique: true,
            ...options,
            references: [],
          },
    );
    if (!field) {
      return undefined as ListField<E, O>;
    }

    return {
      get: () => [...field.references],
      set: (...elements) => {
        this.updateListFieldReferences([fieldName], elements);
      },
      setFrom: (from: L | NonEmptyArray<L>, ...elements) => {
        this.moveListFieldReferences(array(from), [fieldName], elements);
      },
      push: (...elements) => {
        this.updateListFieldReferences(
          [fieldName],
          elements,
          (fieldReferences, ...references) => {
            fieldReferences.push(...references);
            return [];
          },
        );
      },
      pushFrom: (from: L | NonEmptyArray<L>, ...elements) => {
        this.moveListFieldReferences(
          array(from),
          [fieldName],
          elements,
          'push',
        );
      },
      unshift: (...elements) => {
        this.updateListFieldReferences(
          [fieldName],
          elements,
          (fieldReferences, ...referencs) => {
            fieldReferences.unshift(...referencs);
            return [];
          },
        );
      },
      unshiftFrom: (from: L | NonEmptyArray<L>, ...elements) => {
        this.moveListFieldReferences(
          array(from),
          [fieldName],
          elements,
          'unshift',
        );
      },
      splice: (start, deleteCount, ...elements) => {
        this.updateListFieldReferences(
          [fieldName],
          elements,
          (fieldReferences, ...references) =>
            fieldReferences.splice(start, deleteCount, ...references),
        );
      },
      prune: () => {
        this.pruneListFieldReferences([fieldName]);
      },
      getCursor: () => field.cursor,
      setCursor: (cursor: Cursor) => {
        this.setListFieldCursor([fieldName], cursor);
      },
      isStale: () => field.isStale,
      setIsStale: (isStale: boolean) => {
        this.setIsStale(this.listFields, [fieldName], isStale);
      },
      isCached: () => field.listener === 0,
      addListener: () => {
        this.addListener(this.listFields, [fieldName]);
      },
      removeListener: () => {
        this.removeListFieldListener([fieldName]);
      },
    };
  }

  lists<
    O extends
      | (Partial<RepositoryListField<E, Date>> & { references?: never })
      | null
      | undefined = undefined,
  >(fieldNames: NonEmptyArray<L>, options?: O): ListFields<E, O> {
    if (
      fieldNames
        .map((fieldName) =>
          this.putFieldIfAbsent(
            this.listFields,
            fieldName,
            options === null
              ? null
              : {
                  cursor: undefined,
                  isStale: true,
                  listener: 0,
                  cachedAt: new Date(),
                  cacheLimit: DEFAULT_CACHE_LIMIT,
                  isUnique: true,
                  ...options,
                  references: [],
                },
          ),
        )
        .some((f) => f === undefined)
    ) {
      return undefined as ListFields<E, O>;
    }

    return {
      set: (...elements) => {
        this.updateListFieldReferences(fieldNames, elements);
      },
      setFrom: (from: L | NonEmptyArray<L>, ...elements) => {
        this.moveListFieldReferences(array(from), fieldNames, elements);
      },
      push: (...elements) => {
        this.updateListFieldReferences(
          fieldNames,
          elements,
          (fieldReferences, ...references) => {
            fieldReferences.push(...references);
            return [];
          },
        );
      },
      pushFrom: (from: L | NonEmptyArray<L>, ...elements) => {
        this.moveListFieldReferences(array(from), fieldNames, elements, 'push');
      },
      unshift: (...elements) => {
        this.updateListFieldReferences(
          fieldNames,
          elements,
          (fieldReferences, ...referencs) => {
            fieldReferences.unshift(...referencs);
            return [];
          },
        );
      },
      unshiftFrom: (from: L | NonEmptyArray<L>, ...elements) => {
        this.moveListFieldReferences(
          array(from),
          fieldNames,
          elements,
          'unshift',
        );
      },
      splice: (start, deleteCount, ...elements) => {
        this.updateListFieldReferences(
          fieldNames,
          elements,
          (fieldReferences, ...references) =>
            fieldReferences.splice(start, deleteCount, ...references),
        );
      },
      prune: () => {
        this.pruneListFieldReferences(fieldNames);
      },
      setCursor: (cursor: Cursor) => {
        this.setListFieldCursor(fieldNames, cursor);
      },
      setIsStale: (isStale: boolean) => {
        this.setIsStale(this.listFields, fieldNames, isStale);
      },
      addListener: () => {
        this.addListener(this.listFields, fieldNames);
      },
      removeListener: () => {
        this.removeListFieldListener(fieldNames);
      },
    };
  }

  // FIXME: allow passing filter to stale, clear, lists, maps? directly
  findFieldNames(
    filter: (fieldName: string) => boolean,
    { list = true, map = true } = {},
  ) {
    let result: string[] = [];
    if (list) {
      result = result.concat(Object.keys(this.listFields).filter(filter));
    }
    if (map) {
      result = result.concat(Object.keys(this.mapFields).filter(filter));
    }
    return result;
  }

  private addReferenceInFields(
    type: FieldType.Maps,
    fieldNames: M[],
    elementOrId: E | string,
  ): E | null | undefined;
  private addReferenceInFields(
    type: FieldType.Lists,
    fieldNames: L[],
    elementOrId: E | string,
  ): E | null | undefined;
  private addReferenceInFields(
    type: FieldType,
    fieldNames: string[],
    elementOrId: E | string,
  ): E | null | undefined {
    const elementId =
      typeof elementOrId === 'string' ? elementOrId : elementOrId.id();

    let value = this.table[elementId];
    if (typeof elementOrId !== 'string') {
      if (value) {
        value.element.update(elementOrId);
      } else if (value === undefined && fieldNames.length > 0) {
        this.table[elementId] = {
          element: elementOrId.clone(),
          [FieldType.Maps]: {} as Record<M, number>,
          [FieldType.Lists]: {} as Record<L, number>,
        };
        value = this.table[elementId];
      }
    }

    if (value) {
      const counters = value[type] as Record<string, number>;
      fieldNames.forEach((fieldName) => {
        counters[fieldName] = (counters[fieldName] ?? 0) + 1;
      });
    }

    return value ? value.element : value;
  }

  private removeReferencesInField(
    type: FieldType.Maps,
    fieldName: M,
    elements: E[],
  ): void;
  private removeReferencesInField(
    type: FieldType.Lists,
    fieldName: L,
    elements: E[],
  ): void;
  private removeReferencesInField(
    type: FieldType,
    fieldName: string,
    elements: E[],
  ): void {
    elements.forEach((element) => {
      const value = this.table[element.id()];
      if (value) {
        const counters = value[type] as Record<string, number>;
        if (counters[fieldName]) {
          counters[fieldName] -= 1;
          if ((counters[fieldName] as number) <= 0) {
            delete counters[fieldName];
          }
        }

        if (
          Object.keys(value[FieldType.Maps]).length === 0 &&
          Object.keys(value[FieldType.Lists]).length === 0
        ) {
          delete this.table[element.id()];
          this.onElementPrune?.(element);
        }
      }
    });
  }

  private pruneCacheFields() {
    type FieldMap = {
      map: { fieldName: M; field: RepositoryMapField<E, Date> };
      list: { fieldName: L; field: RepositoryListField<E, Date> };
    };
    type Field<K extends keyof FieldMap = keyof FieldMap> = {
      [F in K]: [
        Record<FieldMap[F]['fieldName'], FieldMap[F]['field']>,
        FieldMap[F]['fieldName'],
        FieldMap[F]['field'],
      ];
    }[K];

    const cachedFields = Object.entries<RepositoryMapField<E, Date>>(
      this.mapFields,
    )
      .map(([fieldName, field]) => [this.mapFields, fieldName, field] as Field)
      .concat(
        Object.entries<RepositoryListField<E, Date>>(this.listFields).map(
          ([fieldName, field]) => [this.listFields, fieldName, field] as Field,
        ),
      )
      .filter(([, , field]) => field.listener === 0);

    if (cachedFields.length > this.fieldLimit) {
      orderBy(cachedFields, ([, , field]) => field.cachedAt, 'desc')
        .slice(this.fieldLimit)
        .forEach(<F extends keyof FieldMap>([fields, fieldName]: Field<F>) => {
          const { references } = fields[fieldName];
          delete fields[fieldName];

          // reduce references
          if (Array.isArray(references)) {
            this.removeReferencesInField(
              FieldType.Lists,
              fieldName as L,
              references,
            );
          } else {
            this.removeReferencesInField(
              FieldType.Maps,
              fieldName as M,
              Object.values(references).filter((r) => r !== null) as E[],
            );
          }
        });
    }
  }

  private putFieldIfAbsent<
    N extends string,
    F extends RepositoryMapField<E, Date> | RepositoryListField<E, Date>,
  >(fields: Record<N, F>, fieldName: N, field: F | null) {
    let f = fields[fieldName];
    if (!f) {
      if (field) {
        fields[fieldName] = field;
        f = fields[fieldName];
      }
    }
    return f;
  }

  private forEachField<
    N extends string,
    F extends RepositoryMapField<E, Date> | RepositoryListField<E, Date>,
  >(
    fields: Record<N, F>,
    fieldNames: N[],
    iteratee: (field: F, fieldName: N) => boolean | void,
  ) {
    return fieldNames.reduce<boolean>((acc: boolean, fieldName: N) => {
      const field = fields[fieldName];
      return field ? iteratee(field, fieldName) || acc : acc;
    }, false);
  }

  private setIsStale<
    N extends string,
    F extends RepositoryMapField<E, Date> | RepositoryListField<E, Date>,
  >(fields: Record<N, F>, fieldNames: N[], isStale: boolean) {
    this.forEachField(fields, fieldNames, (field) => {
      field.isStale = isStale;
    });
  }

  private addListener<
    N extends string,
    F extends RepositoryMapField<E, Date> | RepositoryListField<E, Date>,
  >(fields: Record<N, F>, fieldNames: N[]) {
    this.forEachField(fields, fieldNames, (field) => {
      field.listener++;
    });
  }

  private removeMapFieldListener(fieldNames: M[]) {
    const hasNewCachedFields = this.forEachField(
      this.mapFields,
      fieldNames,
      (field) => {
        field.listener = Math.max(0, field.listener - 1);
        if (field.listener === 0) {
          field.cachedAt = new Date();
          return true;
        }
        return false;
      },
    );
    if (hasNewCachedFields) this.pruneCacheFields();
  }

  private removeListFieldListener(fieldNames: L[]) {
    const hasNewCachedFields = this.forEachField(
      this.listFields,
      fieldNames,
      (field, fieldName) => {
        field.listener = Math.max(0, field.listener - 1);
        if (field.listener === 0) {
          if (field.references.length > field.cacheLimit) {
            this.removeReferencesInField(
              FieldType.Lists,
              fieldName,
              field.references.splice(field.cacheLimit),
            );
          }
          field.cursor = undefined;
          field.cachedAt = new Date();
          return true;
        }
        return false;
      },
    );
    if (hasNewCachedFields) this.pruneCacheFields();
  }

  private updateListFieldReferences(
    fieldNames: L[],
    elements: E[],
    update?: (fieldReferences: E[], ...references: E[]) => E[],
  ) {
    // update cached field cachedAt and get all uncached field
    const uncachedFieldNames = fieldNames.filter((fieldName) => {
      const field = this.listFields[fieldName];
      if (field) {
        if (field.listener > 0) {
          return true;
        }
        field.cachedAt = new Date();
      }
      return false;
    });

    // only update existing elements if all cached, otherwise put if absent
    const references = elements
      .map((e) =>
        this.addReferenceInFields(FieldType.Lists, uncachedFieldNames, e),
      )
      .filter(Boolean) as E[];

    // update uncached field references
    this.forEachField(
      this.listFields,
      uncachedFieldNames,
      (field, fieldName) => {
        if (update) {
          this.removeReferencesInField(
            FieldType.Lists,
            fieldName,
            update(field.references, ...references),
          );
        } else {
          const removed = field.references;
          field.references = [...references];
          this.removeReferencesInField(FieldType.Lists, fieldName, removed);
        }
        if (field.isUnique) {
          const { kept, removed } = field.references.reduce<{
            kept: E[];
            removed: E[];
          }>(
            (acc, cur) => {
              if (acc.kept.includes(cur)) {
                acc.removed.push(cur);
              } else {
                acc.kept.push(cur);
              }
              return acc;
            },
            { kept: [], removed: [] },
          );
          field.references = kept;
          this.removeReferencesInField(FieldType.Lists, fieldName, removed);
        }
      },
    );
  }

  private moveListFieldReferences(
    from: L[],
    to: L[],
    elements: E[],
    by?: 'push' | 'unshift',
  ) {
    // update cached field cachedAt and get all uncached field
    const uncachedFieldNames = to.filter((fieldName) => {
      const field = this.listFields[fieldName];
      if (field) {
        if (field.listener > 0) {
          return true;
        }
        field.cachedAt = new Date();
      }
      return false;
    });

    // only update existing elements if all cached, otherwise put if absent
    const references = elements
      .map((e) =>
        this.addReferenceInFields(FieldType.Lists, uncachedFieldNames, e),
      )
      .filter(Boolean) as E[];

    // remove from source field (before update so that position can be updated if source = to)
    this.forEachField(this.listFields, from, (field, fieldName) => {
      const [kept, removed] = partition(
        field.references,
        (reference) => !references.includes(reference),
      );
      field.references = kept;
      this.removeReferencesInField(FieldType.Lists, fieldName, removed);
    });

    // update uncached field references
    this.forEachField(
      this.listFields,
      uncachedFieldNames,
      (field, fieldName) => {
        if (by) {
          field.references[by](...references);
        } else {
          const removed = field.references;
          field.references = [...references];
          this.removeReferencesInField(FieldType.Lists, fieldName, removed);
        }
        if (field.isUnique) {
          const { kept, removed } = field.references.reduce<{
            kept: E[];
            removed: E[];
          }>(
            (acc, cur) => {
              if (acc.kept.includes(cur)) {
                acc.removed.push(cur);
              } else {
                acc.kept.push(cur);
              }
              return acc;
            },
            { kept: [], removed: [] },
          );
          field.references = kept;
          this.removeReferencesInField(FieldType.Lists, fieldName, removed);
        }
      },
    );
  }

  private pruneListFieldReferences(fieldNames: L[]) {
    this.forEachField(this.listFields, fieldNames, (field, fieldName) => {
      if (field.references.length > field.cacheLimit) {
        this.removeReferencesInField(
          FieldType.Lists,
          fieldName,
          field.references.splice(field.cacheLimit),
        );
      }
    });
  }

  private setListFieldCursor(fieldNames: L[], cursor: Cursor) {
    this.forEachField(this.listFields, fieldNames, (field) => {
      field.cursor = cursor;
    });
  }
}
